"use client";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FormLabel } from "@/components/ui/form";
import { ClientSelectWithModal } from "@/components/ui/selects";
import type { ComposeClientContextValue } from "@/lib/core/crm/types";
import type { Client } from "@/lib/types/database";
import { cn } from "@/lib/utils";
import {
  ChevronDown,
  ChevronRight,
  Mail,
  MousePointerClick,
  Sparkles,
  UserRound,
} from "lucide-react";

interface PersonalizationSectionProps {
  clientState: ComposeClientContextValue;
  hasTemplateFields: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  personalizationReplyLinked: boolean;
  triggerSubtitle: string;
}

function PersonalizationStatus({
  personalizationOrigin,
  personalizationReplyLinked,
  selectedClient,
}: {
  personalizationOrigin: ComposeClientContextValue["personalizationOrigin"];
  personalizationReplyLinked: boolean;
  selectedClient: Client;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2.5 border-b px-3 py-2.5 text-xs leading-snug sm:text-[13px] sm:leading-snug",
        personalizationReplyLinked &&
          "border-emerald-200/90 bg-emerald-50 text-emerald-950 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-50",
        !personalizationReplyLinked &&
          personalizationOrigin === "auto_from_to" &&
          "border-sky-200/90 bg-sky-50 text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/35 dark:text-sky-50",
        !personalizationReplyLinked &&
          personalizationOrigin === "manual_selection" &&
          "border-border bg-muted/50 text-foreground",
        !personalizationReplyLinked &&
          personalizationOrigin === "opened_with_contact" &&
          "border-violet-200/80 bg-violet-50 text-violet-950 dark:border-violet-900/45 dark:bg-violet-950/35 dark:text-violet-50",
        !personalizationReplyLinked &&
          !personalizationOrigin &&
          "border-border bg-muted/30 text-muted-foreground"
      )}
    >
      {personalizationReplyLinked ? (
        <Mail className="mt-0.5 size-4 shrink-0 opacity-75" aria-hidden />
      ) : personalizationOrigin === "auto_from_to" ? (
        <Sparkles className="mt-0.5 size-4 shrink-0 text-sky-600 dark:text-sky-300" aria-hidden />
      ) : personalizationOrigin === "manual_selection" ? (
        <MousePointerClick className="mt-0.5 size-4 shrink-0 opacity-70" aria-hidden />
      ) : personalizationOrigin === "opened_with_contact" ? (
        <UserRound className="mt-0.5 size-4 shrink-0 opacity-80" aria-hidden />
      ) : (
        <UserRound className="mt-0.5 size-4 shrink-0 opacity-60" aria-hidden />
      )}
      <div className="min-w-0 space-y-0.5">
        <p className="font-semibold tracking-tight">
          {personalizationReplyLinked
            ? "Reply personalization"
            : personalizationOrigin === "auto_from_to"
              ? "Automatically applied"
              : personalizationOrigin === "manual_selection"
                ? "Your selection"
                : personalizationOrigin === "opened_with_contact"
                  ? "Compose context"
                  : "Active personalization"}
        </p>
        <p className={cn(!personalizationReplyLinked && !personalizationOrigin && "opacity-90")}>
          {personalizationReplyLinked ? (
            <>
              Variables use{" "}
              <span className="font-medium text-emerald-900 dark:text-emerald-100">
                {selectedClient.name}
              </span>
              , matched from this thread.
            </>
          ) : personalizationOrigin === "auto_from_to" ? (
            <>
              Because <strong>To</strong> has a single CRM recipient, variables use{" "}
              <span className="font-medium">{selectedClient.name}</span>. Pick someone else below if
              you need different placeholders.
            </>
          ) : personalizationOrigin === "manual_selection" ? (
            <>
              <span className="font-medium">{selectedClient.name}</span> is the contact used for
              template variables until you clear or replace them.
            </>
          ) : personalizationOrigin === "opened_with_contact" ? (
            <>
              This compose started with <span className="font-medium">{selectedClient.name}</span>{" "}
              for variable fill-in.
            </>
          ) : (
            <>
              <span className="font-medium">{selectedClient.name}</span> supplies values for
              placeholder text.
            </>
          )}
        </p>
      </div>
    </div>
  );
}

function PersonalizationBadge({
  personalizationOrigin,
  personalizationReplyLinked,
  selectedClient,
}: {
  personalizationOrigin: ComposeClientContextValue["personalizationOrigin"];
  personalizationReplyLinked: boolean;
  selectedClient: Client | null;
}) {
  if (!selectedClient) return null;
  if (personalizationReplyLinked) {
    return (
      <span className="inline-flex rounded-full bg-emerald-600/14 px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:bg-emerald-400/15 dark:text-emerald-100">
        Reply
      </span>
    );
  }
  if (personalizationOrigin === "auto_from_to") {
    return (
      <span className="inline-flex rounded-full bg-sky-600/14 px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-sky-800 dark:bg-sky-400/15 dark:text-sky-100">
        Auto · To
      </span>
    );
  }
  if (personalizationOrigin === "manual_selection") {
    return (
      <span className="inline-flex rounded-full bg-muted px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Manual
      </span>
    );
  }
  if (personalizationOrigin === "opened_with_contact") {
    return (
      <span className="inline-flex rounded-full bg-violet-600/14 px-2 py-px text-[10px] font-semibold uppercase tracking-wide text-violet-900 dark:bg-violet-400/15 dark:text-violet-100">
        Opened with
      </span>
    );
  }
  return null;
}

export function PersonalizationSection({
  clientState,
  hasTemplateFields,
  onOpenChange,
  open,
  personalizationReplyLinked,
  triggerSubtitle,
}: PersonalizationSectionProps) {
  const {
    selectedClient,
    clientOptions,
    clientsLoading,
    isSearching,
    clientSearchTerm,
    personalizationOrigin,
    onClientSelect,
    onClientSearchChange,
  } = clientState;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <div className="rounded-md border border-border/70 overflow-hidden">
        {selectedClient && (
          <PersonalizationStatus
            personalizationOrigin={personalizationOrigin}
            personalizationReplyLinked={personalizationReplyLinked}
            selectedClient={selectedClient}
          />
        )}

        <CollapsibleTrigger className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/30 transition-colors">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium">Personalization</p>
              <PersonalizationBadge
                personalizationOrigin={personalizationOrigin}
                personalizationReplyLinked={personalizationReplyLinked}
                selectedClient={selectedClient}
              />
            </div>
            <p className="text-xs text-muted-foreground pr-6 mt-1">{triggerSubtitle}</p>
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </CollapsibleTrigger>

        <CollapsibleContent className="px-3 pb-3 pt-1 space-y-3">
          <div className="text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded p-2">
            The template button next to the body field can be used anytime. Selecting a client here
            helps auto-populate variables like first_name and company_name.
          </div>

          <div className="space-y-2">
            <FormLabel className="text-sm font-medium">Client (Optional)</FormLabel>
            <ClientSelectWithModal
              value={selectedClient?.id || ""}
              onValueChange={onClientSelect}
              placeholder="Search for a client to auto-fill template variables..."
              items={clientOptions}
              disabled={clientsLoading}
              isSearching={isSearching}
              searchTerm={clientSearchTerm}
              onSearchChange={onClientSearchChange}
              expandInline={true}
            />
            {selectedClient && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{selectedClient.name}</span>
                <span> ({selectedClient.company_name || "Individual"})</span>
              </div>
            )}
          </div>

          {!selectedClient && hasTemplateFields && (
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded p-2">
              Select a client to auto-fill template variables like company_name and first_name.
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
